import type { GroupLifeQuoteVersionItem } from '@/services';

export type VersionLivesDelta = {
  /** Vidas desta versão − vidas da versão anterior. */
  delta: number;
  /** Rótulo curto para a coluna: "−3 vs Cotação". */
  label: string;
  /** Explicação do desvio (tooltip/linha de apoio). */
  reason: string;
};

const versionKey = (version: GroupLifeQuoteVersionItem) =>
  `${version.origin ?? 'CURRENT'}-${version.sourcePolicyId ?? ''}-${version.quotationVersionId}`;

/**
 * Diferença de vidas de cada versão para a ANTERIOR (ordem de criação = o
 * `QuotationVersionId`, crescente no InsureMO). Homologação 09-10: Versões
 * mostrava 611 vidas na cotação e 608 na proposta sem explicar o desvio.
 *
 * A contagem de cada versão é a gravada no cálculo daquela versão — as vidas
 * ficam fora do snapshot versionado, então o motivo possível de afirmar é a
 * alteração de vidas entre as duas versões (inclusão, exclusão ou importação
 * que substituiu a lista).
 */
export function versionLivesDeltas(
  versions: GroupLifeQuoteVersionItem[]
): Map<string, VersionLivesDelta> {
  const chronological = [...versions].sort(
    (a, b) => a.quotationVersionId - b.quotationVersionId
  );
  const deltas = new Map<string, VersionLivesDelta>();
  for (let index = 1; index < chronological.length; index++) {
    const current = chronological[index];
    const previous = chronological[index - 1];
    const delta = current.lives - previous.lives;
    if (delta === 0) continue;
    const amount = Math.abs(delta);
    const plural = amount === 1 ? 'vida' : 'vidas';
    const previousLabel = previous.date
      ? `${previous.type} de ${previous.date}`
      : previous.type;
    deltas.set(versionKey(current), {
      delta,
      label: `${delta > 0 ? '+' : '−'}${amount} vs ${previous.type}`,
      reason:
        `${amount} ${plural} a ${delta > 0 ? 'mais' : 'menos'} que a versão anterior (${previousLabel}). ` +
        `As vidas foram ${delta > 0 ? 'incluídas' : 'excluídas'} entre as duas versões — por inclusão/exclusão manual ou por importação de planilha que substituiu a lista.`,
    });
  }
  return deltas;
}

export { versionKey as versionLivesDeltaKey };
